import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Loader2, RefreshCw, Trash2, X, UserCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination, type PageMeta } from '@/components/Pagination';
import { apiCall, type CheckIn } from '@/lib/api';

function relativeTime(iso: string): string {
  // Kiosk rows are ISO with a zone (…Z); dashboard/public rows are SQLite 'YYYY-MM-DD HH:MM:SS' UTC.
  const s = iso.replace(' ', 'T');
  const d = new Date(/(Z|[+-]\d\d:?\d\d)$/.test(s) ? s : s + 'Z');
  const secs = Math.round((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

export default function CheckIns() {
  const { getToken } = useAuth();
  const [items, setItems] = useState<CheckIn[]>([]);
  const [pageMeta, setPageMeta] = useState<PageMeta | null>(null);
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState<'48h' | 'all'>('48h');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const token = await getToken();
      const query: Record<string, string | number> = { page, per_page: 50 };
      if (scope === '48h') {
        query.from = new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString();
      }
      const r = await apiCall<{ items: CheckIn[] } & PageMeta>('/v1/check_ins', {
        bearerToken: token, query,
      });
      setItems(r.items);
      setPageMeta({ page: r.page, per_page: r.per_page, total: r.total, total_pages: r.total_pages });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
    } finally {
      setLoading(false);
    }
  }, [getToken, page, scope]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); }, [scope]);

  async function doDelete(id: string) {
    const token = await getToken();
    await apiCall(`/v1/check_ins/${id}`, { method: 'DELETE', bearerToken: token });
    setItems((prev) => prev.filter((x) => x.id !== id));
    setPendingDelete(null);
  }

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Check-ins</h1>
          <p className="text-sm text-muted-foreground">
            {pageMeta ? `${pageMeta.total} total` : 'Loading…'} · public page at{' '}
            <a href="/checkin" target="_blank" rel="noreferrer" className="underline">/checkin</a>.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <div className="border-b flex items-center gap-1">
        {(['48h', 'all'] as const).map((s) => (
          <button
            key={s} onClick={() => setScope(s)}
            className={
              'px-3 py-2 text-sm border-b-2 -mb-px ' +
              (scope === s ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')
            }>{s === '48h' ? 'Last 48 hours' : 'All time'}</button>
        ))}
      </div>

      {err && <Card><CardContent className="py-4 text-sm text-destructive">{err}</CardContent></Card>}

      {!err && !loading && items.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            <UserCheck className="mx-auto mb-3 h-8 w-8 opacity-40" />
            {scope === '48h' ? 'No check-ins in the last 48 hours.' : 'No check-ins yet.'}
          </CardContent>
        </Card>
      )}

      {items.length > 0 && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="px-3 py-2 font-medium">Visitor</th>
                  <th className="px-3 py-2 font-medium">Class</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Source</th>
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => {
                  const isDel = pendingDelete === c.id;
                  const name = c.member_name ?? c.day_pass_name ?? c.visitor_name ?? '(unknown)';
                  const kind = c.member_id ? 'member' : c.day_pass_id ? 'day pass' : 'guest';
                  return (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="px-3 py-2 font-medium">{name}</td>
                      <td className="px-3 py-2 text-xs">{c.class_name ?? <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-3 py-2"><Badge variant={c.member_id ? 'default' : 'secondary'}>{kind}</Badge></td>
                      <td className="px-3 py-2"><Badge variant="outline">{c.source}</Badge></td>
                      <td className="px-3 py-2 text-xs text-muted-foreground" title={c.checked_in_at}>{relativeTime(c.checked_in_at)}</td>
                      <td className="px-3 py-2 text-right">
                        {isDel ? (
                          <div className="inline-flex items-center gap-1">
                            <Button variant="destructive" size="sm" className="h-7 px-2" onClick={() => void doDelete(c.id)}>Yes</Button>
                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setPendingDelete(null)}><X className="h-3.5 w-3.5" /></Button>
                          </div>
                        ) : (
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground hover:text-destructive" onClick={() => setPendingDelete(c.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Pagination meta={pageMeta} onPageChange={setPage} itemLabel="check-ins" />
    </div>
  );
}
